import { useEffect, useRef, useState } from "react";
import { CONTACT_EMAIL, WAITLIST_URL } from "../config";
import { SiteMotion } from "../components/Motion";
import { CoveScene } from "../components/CoveScene";
import { ProductDemo } from "../components/ProductDemo";

/* Landing page v3 — structure kb/18 §4 + kb/24, art kb/25, voice + copy from
   the brand guide (kb/26). Facts: account required, free 14 days no card,
   browser today / Mac next, spatial applies to local+direct audio only. */

const CTA_GLOW =
  "transition-all duration-200 hover:brightness-105 hover:shadow-[0_0_28px_rgba(255,180,84,0.35)] hover:-translate-y-px active:translate-y-0";

const CARD_HOVER =
  "transition-all duration-300 hover:-translate-y-1 hover:border-accent/30 hover:bg-panel2 hover:shadow-[inset_0_1px_0_rgba(255,180,84,0.12),0_12px_32px_-16px_rgba(0,0,0,0.6)]";

const GRAIN_URI =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/%3E%3C/filter%3E%3Crect width='160' height='160' filter='url(%23n)'/%3E%3C/svg%3E\")";

function LogoMark({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 32 32"
      fill="none"
      aria-hidden="true"
      className={className}
    >
      <path
        d="M6 28 V16 C6 9.9 10.5 5 16 5 s10 4.9 10 11 v12"
        stroke="var(--color-accent)"
        strokeWidth="2.6"
        strokeLinecap="round"
      />
      <path
        d="M11.5 15.5 q2.2 -2.6 4.5 0 t4.5 0"
        stroke="var(--color-accent)"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <path
        d="M11.5 20.5 q2.2 -2.6 4.5 0 t4.5 0"
        stroke="var(--color-accent-soft)"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <path
        d="M11.5 25.5 q2.2 -2.6 4.5 0 t4.5 0"
        stroke="var(--color-accent)"
        strokeWidth="2"
        strokeLinecap="round"
        opacity="0.7"
      />
    </svg>
  );
}

const NAV_LINKS = [
  { href: "#how", label: "How it works" },
  { href: "#cove", label: "The cove" },
  { href: "#journey", label: "The journey" },
  { href: "#faq", label: "FAQ" },
];

function Nav() {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const menu = menuRef.current;
    const background = Array.from(document.querySelectorAll<HTMLElement>("main, footer, .nav-shell"));
    const previousInert = background.map((node) => node.inert);
    background.forEach((node) => { node.inert = true; });
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    menu?.querySelector<HTMLButtonElement>("button")?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") { event.preventDefault(); setOpen(false); }
      if (event.key !== "Tab" || !menu) return;
      const items = Array.from(menu.querySelectorAll<HTMLElement>("a[href], button"));
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
      if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    };
    const desktop = matchMedia("(min-width: 768px)");
    const onResize = () => { if (desktop.matches) setOpen(false); };
    document.addEventListener("keydown", onKeyDown);
    desktop.addEventListener("change", onResize);
    return () => {
      background.forEach((node, i) => { node.inert = previousInert[i]; });
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", onKeyDown);
      desktop.removeEventListener("change", onResize);
      requestAnimationFrame(() => triggerRef.current?.focus({ preventScroll: true }));
    };
  }, [open]);
  return (
    <header id="site-nav" className="fixed inset-x-0 top-0 z-50">
      <div className="nav-veil" />
      <div className="nav-shell mx-auto flex w-full max-w-6xl items-center justify-between px-6 py-6">
        <a href="/" className="flex items-center gap-2.5">
          <LogoMark className="logo-mark h-7 w-7" />
          <span className="text-[15px] font-semibold tracking-[0.02em] text-ink">
            Kovrie
          </span>
        </a>
        <nav className="hidden items-center gap-8 md:flex">
          {NAV_LINKS.map((link) => (
            <a
              key={link.href}
              href={link.href}
              className="nav-link text-sm font-medium text-ink/60 transition-colors duration-200 hover:text-ink"
            >
              {link.label}
            </a>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <a
            href={WAITLIST_URL}
            className={`nav-cta hidden rounded-full border border-ink/25 bg-ink/5 px-4 py-2 text-[13px] font-semibold text-ink backdrop-blur-md md:inline-block ${CTA_GLOW}`}
          >
            Join the beta
          </a>
          <button
            type="button"
            ref={triggerRef}
            aria-label="Open menu"
            aria-controls="mobile-navigation"
            aria-expanded={open}
            onClick={() => setOpen(!open)}
            className="flex h-11 w-11 flex-col items-center justify-center gap-[5px] md:hidden"
          >
            <span
              className={`h-px w-4 bg-ink transition-transform duration-300 ${open ? "translate-y-[3px] rotate-45" : ""}`}
            />
            <span
              className={`h-px w-4 bg-ink transition-transform duration-300 ${open ? "-translate-y-[3px] -rotate-45" : ""}`}
            />
          </button>
        </div>
      </div>
      {open && <div
        ref={menuRef}
        id="mobile-navigation"
        role="dialog"
        aria-modal="true"
        aria-label="Navigation menu"
        className="fixed inset-0 z-10 flex flex-col gap-8 overflow-y-auto bg-bg0 px-6 pb-10 pt-6 md:hidden"
      >
        <button type="button" onClick={() => setOpen(false)} className="min-h-11 self-end rounded-full border border-line2 px-4 text-ink">
          Close menu
        </button>
        <nav aria-label="Mobile" className="flex flex-col gap-4 px-2">
          {NAV_LINKS.map((link) => (
            <a key={link.href} href={link.href} onClick={() => setOpen(false)} className="py-2 text-2xl font-semibold text-ink">
              {link.label}
            </a>
          ))}
          <a href="/terms/" className="py-2 text-base text-muted">Terms</a>
          <a href="/privacy/" className="py-2 text-base text-muted">Privacy</a>
        </nav>
        <a href={WAITLIST_URL} className={`mt-auto rounded-full bg-accent px-6 py-3.5 text-center text-base font-semibold text-bg0 ${CTA_GLOW}`}>
          Join the first listeners
        </a>
      </div>}
    </header>
  );
}

function Eyebrow(props: { tone: "second" | "accent"; children: string }) {
  return (
    <p
      data-reveal
      className={`text-xs font-semibold uppercase tracking-[0.25em] ${props.tone === "second" ? "text-second" : "text-accent"}`}
    >
      {props.children}
    </p>
  );
}

function Hero() {
  return (
    <section className="relative min-h-[100svh] overflow-hidden">
      <div id="hero-art" className="absolute inset-0 will-change-transform">
        <img
          src="/img/hero-cove.jpg"
          alt="Inside a warm painted den: an armchair with a blanket, a steaming mug, plants and records on shelves, headphones hanging beside a wide window full of rain and teal night"
          className="h-full w-full object-cover object-[center_38%]"
        />
      </div>
      <div
        id="hero-breath"
        aria-hidden="true"
        className="absolute inset-0 opacity-50"
        style={{
          background:
            "radial-gradient(60% 55% at 62% 46%, rgba(255,180,84,0.16), transparent 70%)",
        }}
      />
      <div
        aria-hidden="true"
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(180deg, rgba(14,15,18,0.55) 0%, rgba(14,15,18,0.1) 30%, rgba(14,15,18,0.22) 62%, rgba(14,15,18,0.92) 100%)",
        }}
      />
      <div className="relative z-10 mx-auto flex min-h-[100svh] w-full max-w-6xl flex-col justify-end px-6 pb-20 pt-28">
        <div id="hero-copy" className="max-w-3xl">
          <h1 className="text-balance text-4xl font-semibold leading-[1.05] tracking-[-0.025em] text-ink md:text-6xl xl:text-7xl">
            Music, talk, ambience. Too many apps for one little cove of
            sound.{" "}
            <span className="text-accent">So I built Kovrie.</span>
          </h1>
          <p className="mt-6 max-w-xl text-lg leading-relaxed text-ink/80">
            Kovrie mixes what you already listen to. Spotify, YouTube, your
            own files, all in one place. A cove is just that place: yours, at
            your own levels.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-4">
            <a
              href={WAITLIST_URL}
              className={`rounded-full bg-accent px-6 py-3 text-base font-semibold text-bg0 ${CTA_GLOW}`}
            >
              Join the first listeners
            </a>
            <a
              href="#preview"
              className="rounded-full border border-ink/30 px-6 py-3 text-base text-ink/90 backdrop-blur-sm transition-colors hover:border-accent hover:text-ink"
            >
              Try the controls
            </a>
          </div>
          <p className="mt-5 text-sm text-ink/60">
            Free for 14 days when you sign up · No card · Runs in your browser
          </p>
        </div>
      </div>
    </section>
  );
}

function FounderStory() {
  return (
    <section className="relative overflow-hidden bg-bg1">
      <div
        aria-hidden="true"
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(50% 60% at 72% 50%, rgba(255,180,84,0.07), transparent 70%)",
        }}
      />
      <img
        src="/img/nook-dark.jpg"
        alt="Painted vignette on darkness: a green armchair with a gold blanket, an amber lamp, headphones and an open book on a side table, a porthole window of moonlit teal sea"
        className="breathing-art absolute right-0 top-1/2 hidden max-h-[85%] w-[46%] -translate-y-1/2 object-contain md:block"
        style={{
          maskImage:
            "radial-gradient(85% 85% at 55% 50%, black 55%, transparent 92%)",
          WebkitMaskImage:
            "radial-gradient(85% 85% at 55% 50%, black 55%, transparent 92%)",
        }}
      />
      <div className="relative z-10 mx-auto w-full max-w-6xl px-6 py-32 md:py-40">
        <Eyebrow tone="accent">The maker</Eyebrow>
        <div data-reveal className="mt-4 max-w-xl">
          <span
            aria-hidden="true"
            className="-mb-4 block font-serif text-7xl leading-none text-accent/30"
          >
            "
          </span>
          <p className="text-2xl font-normal leading-[1.55] text-ink/90 md:text-[28px]">
            I've always worked with sound around me: music in the background,
            a podcast I half-listen to, something ambient filling the rest.
            Doing that
            across three apps and four tabs was a mess: fighting volume
            sliders, nothing remembered my levels, nothing sat right. So I
            built the mixer I wanted. I'm just building this because I need
            it. I use it every day, and I'm going to keep going. Come along.
          </p>
          <p className="mt-6 text-sm text-dim before:mr-3 before:inline-block before:h-px before:w-6 before:bg-accent/60 before:align-middle">
            Kane, maker of Kovrie
          </p>
        </div>
        <img
          src="/img/nook-dark.jpg"
          alt=""
          aria-hidden="true"
          className="mt-10 w-full rounded-2xl md:hidden"
        />
      </div>
    </section>
  );
}

function HowItWorks() {
  const steps = [
    {
      title: "Bring your sound.",
      body: "Paste a YouTube link, connect your Spotify, drop in your own files. Each one becomes a card, a layer you can hold. Videos pop out to watch, then tuck away when you're back to work.",
      motif: (
        <div data-reveal-group className="mb-5 flex items-center gap-2">
          <span className="h-2.5 w-2.5 rounded-full bg-spotify" />
          <span className="h-2.5 w-2.5 rounded-full bg-youtube" />
          <span className="h-2.5 w-2.5 rounded-full bg-local" />
        </div>
      ),
    },
    {
      title: "Give each layer a place.",
      body: "Drop it in a cove (Background, Talk, Ambient, or one you make) and set its presence: in your face, or just… around.",
      motif: (
        <div className="mb-5 h-1.5 w-full max-w-28 overflow-hidden rounded-full bg-panel2">
          <div
            data-presence={65}
            style={{ width: "65%" }}
            className="h-full rounded-full bg-accent shadow-[0_0_10px_rgba(255,180,84,0.4)]"
          />
        </div>
      ),
    },
    {
      title: "Save the blend.",
      body: "Snapshot the whole arrangement and get yesterday's perfect mix back in one click.",
      motif: (
        <span className="relative mb-5 flex h-2.5 w-2.5">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-accent opacity-60" />
          <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-accent" />
        </span>
      ),
    },
  ];
  return (
    <section id="how" className="mx-auto w-full max-w-5xl px-6 py-32 2xl:max-w-6xl">
      <Eyebrow tone="second">How it works</Eyebrow>
      <h2
        data-reveal
        className="mt-3 text-4xl font-semibold tracking-[-0.02em] text-ink md:text-5xl"
      >
        Three moves, then music
      </h2>
      <div data-reveal-group className="mt-10 grid gap-6 md:grid-cols-3 2xl:gap-8">
        {steps.map((step, i) => (
          <div
            key={step.title}
            className={`relative overflow-hidden rounded-2xl border border-line bg-panel p-6 ${CARD_HOVER}`}
          >
            <span
              aria-hidden="true"
              className="absolute right-5 top-4 select-none text-7xl font-semibold leading-none text-accent/[0.08]"
            >
              {i + 1}
            </span>
            {step.motif}
            <h3 className="mt-2 text-lg font-semibold text-ink">{step.title}</h3>
            <p className="mt-2 leading-relaxed text-muted">{step.body}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function Positioning() {
  return (
    <section className="mx-auto w-full max-w-3xl px-6 py-24">
      <h2 data-reveal className="text-3xl font-semibold tracking-tight text-ink">
        Not another soundscape app
      </h2>
      <div data-reveal-group className="mt-4 flex items-center gap-2">
        <span className="h-2.5 w-2.5 rounded-full bg-spotify" />
        <span className="h-2.5 w-2.5 rounded-full bg-youtube" />
        <span className="h-2.5 w-2.5 rounded-full bg-local" />
      </div>
      <p data-reveal className="mt-4 text-lg leading-relaxed text-muted">
        Focus-sound apps hand you their library and their algorithm. Kovrie is a
        mixer. It plays <em className="not-italic text-ink">your</em> sources:
        the playlist you love, the talk that keeps you going, the rain loop
        you've used for years, all together, at levels you choose. I don't
        supply the soundtrack. You already have it.
      </p>
    </section>
  );
}

const HOOK_WORDS =
  "Working alone doesn't have to feel like working alone. A voice in the room, music under it, weather outside the window. It changes the whole space.".split(" ");

function EmotionalHook() {
  return (
    <section className="mx-auto w-full max-w-3xl px-6 py-40 text-center md:py-48">
      <p data-word-reveal className="text-3xl leading-relaxed text-ink md:text-4xl lg:text-5xl">
        {HOOK_WORDS.map((word, i) => (
          <span key={i}>{word} </span>
        ))}
      </p>
    </section>
  );
}

function SpatialCove() {
  return (
    <section id="cove" className="relative overflow-hidden border-y border-line bg-bg0">
      <div className="relative mx-auto max-w-[1680px]">
        <img
          src="/img/cove-room.jpg"
          alt="A painted cave-cove listening den: rugs and cushions under string lights, a record player and headphones beside an amber lamp, the cave mouth opening onto moonlit teal water"
          className="absolute inset-y-0 right-0 hidden h-full w-[62%] object-cover object-[65%_center] md:block"
          style={{
            maskImage:
              "radial-gradient(120% 110% at 72% 50%, black 55%, transparent 85%)",
            WebkitMaskImage:
              "radial-gradient(120% 110% at 72% 50%, black 55%, transparent 85%)",
          }}
        />
        <div
          aria-hidden="true"
          className="absolute inset-0 hidden md:block"
          style={{
            background:
              "linear-gradient(90deg, rgba(14,15,18,0.95) 0%, rgba(14,15,18,0.6) 40%, transparent 60%)",
          }}
        />
        <CoveScene />
        <div className="pointer-events-none relative z-10 mx-auto flex min-h-[600px] w-full max-w-6xl items-center px-6 py-16 lg:min-h-[680px] 2xl:min-h-[760px]">
          <div className="pointer-events-auto max-w-xl">
            <Eyebrow tone="second">The premium moment</Eyebrow>
            <h2
              data-reveal
              className="mt-3 text-4xl font-semibold tracking-[-0.02em] text-ink md:text-5xl"
            >
              Step into the cove
            </h2>
            <p data-reveal className="mt-6 text-lg leading-relaxed text-muted">
              Kovrie's spatial mode places your layers{" "}
              <em className="not-italic text-ink">around</em> you: the talk
              track just off your shoulder, rain far behind, music low in
              front. Real binaural 3D, on the sounds you own. Headphones on.
            </p>
            <p data-reveal className="mt-6 text-sm leading-relaxed text-dim">
              Straight talk: spatial applies to your local files and direct
              audio. YouTube and Spotify layers ride along at volume only.
            </p>
          </div>
        </div>
        <img
          src="/img/cove-room.jpg"
          alt=""
          aria-hidden="true"
          className="mx-6 mb-10 w-[calc(100%-3rem)] rounded-2xl md:hidden"
        />
      </div>
    </section>
  );
}

function StatusDot({ tone }: { tone: "shipped" | "committed" | "exploring" }) {
  if (tone === "shipped") {
    return <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />;
  }
  if (tone === "committed") {
    return <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full border border-accent/60" />;
  }
  return (
    <span className="relative mt-1.5 flex h-1.5 w-1.5 shrink-0">
      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-accent/50" />
      <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-accent/40" />
    </span>
  );
}

function Journey() {
  const columns = [
    {
      title: "Now",
      note: "shipped or in the current build",
      tone: "shipped" as const,
      items: [
        "The mixer: coves, presence, warmth, snapshots, playlists",
        "Pop-out video for YouTube layers",
        "The spatial cove (premium): real binaural 3D",
        "Runs in your browser",
        "Themes: Dusk · Hearth · Daybreak · Windward",
      ],
    },
    {
      title: "Next",
      note: "committed",
      tone: "committed" as const,
      items: [
        "First public release",
        "Mac desktop app",
        "Windows & Linux after that",
      ],
    },
    {
      title: "Exploring",
      note: "the fun part, no promises, real intent",
      tone: "exploring" as const,
      items: [
        "Customize your cove: quotes, vision boards, your own den",
        "Scheduled blends: morning, afternoon, night",
        "Meetings inside the cove: the call becomes a layer",
        "Video layers · visionOS & AR someday",
      ],
    },
  ];
  return (
    <section id="journey" className="mx-auto w-full max-w-5xl px-6 py-32 2xl:max-w-6xl">
      <Eyebrow tone="accent">The road ahead</Eyebrow>
      <h2
        data-reveal
        className="mt-3 text-4xl font-semibold tracking-[-0.02em] text-ink md:text-5xl"
      >
        Built for the long haul
      </h2>
      <p data-reveal className="mt-5 max-w-2xl text-lg leading-relaxed text-muted">
        Kovrie is one person building the thing I use every day. No growth
        team, no investors to impress, not a cash grab. I need this app to
        exist, so I'm making it. The roadmap is public, and the people who join
        early get to shape it. Help me on the journey.
      </p>
      <div data-reveal-group className="mt-10 grid gap-6 md:grid-cols-3 2xl:gap-8">
        {columns.map((col) => (
          <div
            key={col.title}
            className={`rounded-2xl border border-line2 bg-panel p-6 ${CARD_HOVER}`}
          >
            <h3 className="text-xl font-semibold text-accent">{col.title}</h3>
            <p className="text-sm text-muted">{col.note}</p>
            <ul className="mt-5 flex flex-col gap-3.5">
              {col.items.map((item) => (
                <li key={item} className="flex items-start gap-2.5 font-medium leading-relaxed text-ink">
                  <StatusDot tone={col.tone} />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      <p data-reveal className="mt-8 text-sm text-dim">
        Got an idea for your cove?{" "}
        <a href={`mailto:${CONTACT_EMAIL}`} className="text-accent">
          Tell me. It goes on the board.
        </a>
      </p>
    </section>
  );
}

function TrustStrip() {
  const items = [
    {
      title: "Your blend, remembered",
      body: "Snapshots save the whole arrangement (every layer, level, and presence) and bring it back in one click.",
    },
    {
      title: "More than a volume knob",
      body: "Presence adjusts loudness and, for files and direct audio, tone. Spotify and YouTube use volume only. Pop a video out to watch; tuck it back when you're done.",
    },
    {
      title: "Free to try, honestly",
      body: "Sign up and everything's free for 14 days. No card. After that you keep a free tier that stays genuinely useful, and the first listeners get founding prices.",
    },
  ];
  return (
    <section
      className="mx-auto grid w-full max-w-5xl gap-6 px-6 pb-24 md:grid-cols-3 2xl:max-w-6xl 2xl:gap-8"
      data-reveal-group
    >
      {items.map((item) => (
        <div
          key={item.title}
          className={`rounded-2xl border border-line p-6 ${CARD_HOVER}`}
        >
          <h3 className="text-lg font-semibold text-ink">{item.title}</h3>
          <p className="mt-2 leading-relaxed text-muted">{item.body}</p>
        </div>
      ))}
    </section>
  );
}

function Faq() {
  const items = [
    {
      q: "What's a cove, exactly?",
      a: "A group of layers with its own place in your mix: Background, Talk, Ambient, or anything you create. Set a cove's volume and presence once and every layer inside follows. It's also what the whole thing becomes: your own little cove of sound.",
    },
    {
      q: 'What is "presence"?',
      a: "Presence changes loudness and, for your files and direct audio, tone. Low presence settles into the background; high presence steps forward. Spotify and YouTube use volume only. Placement is a separate control for distance and, on supported audio, direction.",
    },
    {
      q: "Do I need an account?",
      a: "Yes, a free one, no card. Signing up starts your 14 days of everything.",
    },
    {
      q: "Is it really free?",
      a: "For 14 days, everything is, and there's no card to enter. After that you keep a free tier, and paid unlocks the full mixer and the spatial cove. I'm an indie developer, not a subscription trap. The free tier is meant to stay useful.",
    },
    {
      q: "What happens after the 14 days?",
      a: 'You drop to the free tier. I\'m still settling the exact limits, but here\'s the promise: it stays genuinely useful. No hostage screens, no "your blends are locked until you pay." And the first listeners get founding prices when paid opens.',
    },
    {
      q: "What platforms does it run on?",
      a: "Your browser, today. The Mac desktop app is next, with Windows and Linux planned after that.",
    },
    {
      q: "How does Spotify work?",
      a: "You connect your own Spotify account and your playlists become layers. Premium is the reliable path today. I'm still testing what free accounts can do, so I won't promise that yet. YouTube and your own files work for everyone.",
    },
  ];
  return (
    <section id="faq" className="mx-auto w-full max-w-3xl px-6 py-24">
      <h2 data-reveal className="text-3xl font-semibold tracking-tight text-ink">
        Questions
      </h2>
      <div data-reveal className="mt-8 divide-y divide-line border-y border-line">
        {items.map((item) => (
          <details key={item.q} className="group py-4">
            <summary className="flex min-h-11 cursor-pointer list-none items-center justify-between gap-4 text-ink">
              <span className="font-medium">{item.q}</span>
              <svg
                aria-hidden="true"
                viewBox="0 0 16 16"
                className="h-4 w-4 shrink-0 text-dim transition-transform duration-300 group-open:rotate-180"
              >
                <path
                  d="M3 6l5 5 5-5"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </summary>
            <p className="pb-2 pt-2 leading-relaxed text-muted">{item.a}</p>
          </details>
        ))}
      </div>
    </section>
  );
}

function FinalCta() {
  return (
    <section className="relative overflow-hidden">
      <img
        src="/img/og-cove.jpg"
        alt=""
        aria-hidden="true"
        className="breathing-art absolute inset-0 h-full w-full object-cover"
      />
      <div
        aria-hidden="true"
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(180deg, rgba(14,15,18,0.92) 0%, rgba(14,15,18,0.55) 45%, rgba(14,15,18,0.75) 100%)",
        }}
      />
      <div className="relative z-10 mx-auto w-full max-w-3xl px-6 py-40 text-center">
        <h2
          data-reveal
          className="text-4xl font-semibold tracking-[-0.02em] text-ink md:text-5xl"
        >
          Build your own little cove of sound
        </h2>
        <p data-reveal className="mt-4 text-lg text-ink/80">
          Kovrie is almost ready. The first listeners get in first, shape the
          roadmap, and get founding prices when the doors open.
        </p>
        <div data-reveal className="mt-8">
          <a
            href={WAITLIST_URL}
            className={`inline-block rounded-full bg-accent px-8 py-3.5 text-base font-semibold text-bg0 ${CTA_GLOW}`}
          >
            Join the first listeners
          </a>
          <p className="mt-4 text-sm text-ink/55">
            Just your email. I'll write when it's ready. No drip campaign.
          </p>
        </div>
      </div>
    </section>
  );
}

export function Footer() {
  return (
    <footer className="border-t border-line">
      <div className="mx-auto flex w-full max-w-5xl flex-wrap items-center justify-between gap-4 px-6 py-8 text-sm text-dim">
        <div className="flex items-center gap-2.5">
          <LogoMark className="h-5 w-5 opacity-70" />
          <p>Made by one person who needed it. · © 2026 Kovrie</p>
        </div>
        <nav className="flex items-center gap-6">
          <a href="/terms/" className="transition-colors hover:text-ink">
            Terms
          </a>
          <a href="/privacy/" className="transition-colors hover:text-ink">
            Privacy
          </a>
          <a
            href={`mailto:${CONTACT_EMAIL}`}
            className="transition-colors hover:text-ink"
          >
            Contact
          </a>
        </nav>
      </div>
    </footer>
  );
}

export function Home() {
  return (
    <>
      <SiteMotion />
      <div
        aria-hidden="true"
        className="pointer-events-none fixed inset-0 z-[60] opacity-[0.025] mix-blend-overlay"
        style={{ backgroundImage: GRAIN_URI }}
      />
      <Nav />
      <main>
        <Hero />
        <ProductDemo />
        <FounderStory />
        <HowItWorks />
        <Positioning />
        <EmotionalHook />
        <SpatialCove />
        <Journey />
        <TrustStrip />
        <Faq />
        <FinalCta />
      </main>
      <Footer />
    </>
  );
}
